import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Button,
  Fab,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Link,
} from '@mui/material';
import { useProject } from '../contexts/ProjectContext';
import { getWebsiteImportJobs, restartWebsiteImportJob } from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CreateImportJobDialog from '../components/CreateImportJobDialog';
import ReplayIcon from '@mui/icons-material/Replay';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import EditIcon from '@mui/icons-material/Edit';

const statusColors = {
  backlog: 'default',
  processing: 'primary',
  processed: 'success',
  failed: 'error'
};

const StatusIcon = ({ status }) => {
  switch (status) {
    case 'processing':
      return <PendingIcon color="primary" />;
    case 'processed':
      return <CheckCircleIcon color="success" />;
    case 'failed':
      return <ErrorIcon color="error" />;
    default:
      return <ScheduleIcon color="disabled" />;
  }
};

const ImportJobs = () => {
  const { selectedProject } = useProject();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [menuAnchors, setMenuAnchors] = useState({});
  const [selectedJob, setSelectedJob] = useState(null);

  const handleRefresh = () => {
    loadJobs();
  };

  const loadJobs = async () => {
    if (!selectedProject) return;
    
    try {
      setLoading(true);
      const data = await getWebsiteImportJobs(selectedProject.id);
      setJobs(data);
      setError(null);
    } catch (err) {
      setError('Failed to load import jobs');
      console.error('Error loading import jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobs();
  }, [selectedProject]);

  const handleJobCreated = (newJob) => {
    setJobs(prevJobs => [newJob, ...prevJobs]);
  };

  const handleRestartJob = async (jobId) => {
    try {
      const updatedJob = await restartWebsiteImportJob(jobId);
      setJobs(prevJobs => 
        prevJobs.map(job => 
          job.id === jobId ? updatedJob : job
        )
      );
    } catch (error) {
      console.error('Error restarting job:', error);
      // Optionally show an error message to the user
    }
  };

  const handleMenuOpen = (event, jobId) => {
    event.stopPropagation();
    setMenuAnchors(prev => ({
      ...prev,
      [jobId]: event.currentTarget
    }));
  };

  const handleMenuClose = (jobId) => {
    setMenuAnchors(prev => ({
      ...prev,
      [jobId]: null
    }));
  };

  const handleForceRefresh = async (jobId) => {
    try {
      const updatedJob = await restartWebsiteImportJob(jobId);
      setJobs(prevJobs => 
        prevJobs.map(job => 
          job.id === jobId ? updatedJob : job
        )
      );
    } catch (error) {
      console.error('Error force refreshing job:', error);
    }
    handleMenuClose(jobId);
  };

  const handleEditJob = (job) => {
    setSelectedJob(job);
    setOpenDialog(true);
    handleMenuClose(job.id);
  };

  const handleJobSaved = (updatedJob) => {
    setJobs(prevJobs =>
      prevJobs.map(job =>
        job.id === updatedJob.id ? updatedJob : job
      )
    );
  };

  if (!selectedProject) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">
          Please select a project to view import jobs
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Import Jobs
        </Typography>
        <Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenDialog(true)}
          >
            New Import
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" sx={{ mb: 3, p: 2, bgcolor: 'grey.900', borderRadius: 1, border: 1, borderColor: 'grey.700' }}>
        ℹ️ Please note: When importing content, ensure you have proper authorization or are using publicly available resources. Remember to comply with websites' terms of service. For more information about content usage, please review our <Link href="https://writellect.com/terms" sx={{ color: 'primary.main' }}>Terms of Service</Link>.
      </Typography>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={2}>
        {jobs.map((job) => (
          <Grid item xs={12} md={6} lg={4} key={job.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Chip
                    label={job.status}
                    color={statusColors[job.status]}
                    size="small"
                    icon={<StatusIcon status={job.status} />}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                      {formatDistanceToNow(new Date(job.created_at), { addSuffix: true })}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, job.id)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                    <Menu
                      anchorEl={menuAnchors[job.id]}
                      open={Boolean(menuAnchors[job.id])}
                      onClose={() => handleMenuClose(job.id)}
                    >
                      <MenuItem 
                        onClick={() => {
                          handleForceRefresh(job.id);
                          handleMenuClose(job.id);
                        }}
                      >
                        <ListItemIcon>
                          <AutorenewIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Force Refresh</ListItemText>
                      </MenuItem>
                      <MenuItem 
                        onClick={() => handleEditJob(job)}
                      >
                        <ListItemIcon>
                          <EditIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Modify Job</ListItemText>
                      </MenuItem>
                      {job.status === 'failed' && (
                        <MenuItem 
                          onClick={() => {
                            handleRestartJob(job.id);
                            handleMenuClose(job.id);
                          }}
                        >
                          <ListItemIcon>
                            <ReplayIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Retry Failed Job</ListItemText>
                        </MenuItem>
                      )}
                    </Menu>
                  </Box>
                </Box>

                {job.status === 'processing' && (
                 <Box sx={{ width: '100%', mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Processing
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {job.progress}%
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={job.progress || 0}
                      sx={{
                        height: 8,
                        borderRadius: 1,
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 1,
                        }
                      }}
                    />
                  </Box>
                )}

                <Typography variant="subtitle1" component="div" sx={{ mb: 1 }}>
                  {new URL(job.url).hostname}
                </Typography>
                
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    wordBreak: 'break-all',
                    mb: 1
                  }}
                >
                  {job.url}
                </Typography>

                {job.error_message && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    Error: {job.error_message}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={job.fetch_whole_domain ? 'Whole Domain' : 'Single Page'}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    {job.limit && (
                      <Chip
                        label={`Limit: ${job.limit}`}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                    )}
                    {job.scrape_filter && (
                      <Chip
                        label={`Scrape: ${job.scrape_filter}`}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                    )}
                    {job.path_filter && (
                      <Chip
                        label={`Store: ${job.path_filter}`}
                        size="small"
                      />
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {!loading && jobs.length === 0 && (
          <Grid item xs={12}>
            <Typography variant="body1" color="text.secondary">
              No import jobs found. Create one to get started.
            </Typography>
          </Grid>
        )}
      </Grid>

      <CreateImportJobDialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setSelectedJob(null);
        }}
        onJobCreated={selectedJob ? handleJobSaved : handleJobCreated}
        projectId={selectedProject?.id}
        existingJob={selectedJob}
      />
    </Box>
  );
};

export default ImportJobs; 